// Session hook — subscribes to Supabase auth state.
// UI in V1 still renders placeholder/demo data; components can opt in by reading
// `session` here without breaking the demo flows.
import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/lib/services/authService";
import type { AppRole } from "@/lib/db/models";

export interface AuthState {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  loading: boolean;
  isCustomer: boolean;
  isStaff: boolean;
  isAdmin: boolean;
}

export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Register the listener FIRST, then hydrate the current session.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        // Defer role fetch so we don't call other Supabase APIs synchronously
        // inside the auth callback.
        setTimeout(() => {
          authService
            .getRoles(s.user.id)
            .then(setRoles)
            .catch(() => setRoles([]));
        }, 0);
      } else {
        setRoles([]);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        authService
          .getRoles(data.session.user.id)
          .then(setRoles)
          .catch(() => setRoles([]))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  return {
    session,
    user: session?.user ?? null,
    roles,
    loading,
    isCustomer: roles.includes("customer"),
    isStaff: roles.includes("staff"),
    isAdmin: roles.includes("admin"),
  };
}
